<template>
    <v-dialog 
        v-model="dialogVisible" 
        max-width="650px" 
        overlay-color="#08090c" 
        overlay-opacity="0.85"
        content-class="premium-dialog"
    >
        <v-card class="glass-card-dialog overflow-hidden" dark>
            <!-- Close Button overlaid on top of the image corner -->
            <v-btn 
                icon 
                color="white" 
                class="close-btn" 
                @click="$emit('close')"
            >
                <v-icon size="20">mdi-close</v-icon>
            </v-btn>

            <!-- Product Widescreen Image -->
            <div class="product-image-wrapper">
                <v-img 
                    :src="'/img/' + product.picture" 
                    class="product-image" 
                    aspect-ratio="1.8"
                    cover
                ></v-img>
                <div class="product-image-gradient-overlay"></div>
            </div>

            <!-- Details Content Section -->
            <div class="content-column pa-6 pa-sm-8">
                <!-- Title & Share Button -->
                <div class="d-flex align-center justify-space-between mb-4">
                    <h2 class="text-h4 font-weight-black white--text line-height-tight pr-4">
                        {{ product.title }}
                    </h2>
                    <v-btn icon color="#00B074" class="premium-btn-icon-circle flex-shrink-0" @click="shareProduct(product)">
                        <v-icon size="18">mdi-share-variant-outline</v-icon>
                    </v-btn>
                </div>

                <!-- Main Description -->
                <p class="grey--text text--lighten-1 mb-5 text-subtitle-2 font-weight-regular" v-html="product.description" style="line-height: 1.6;"></p>

                <v-divider style="border-color: rgba(255,255,255,0.06) !important;" class="mb-5"></v-divider>

                <!-- Choice list of product variations (Chips) -->
                <div v-if="product.variations.length > 1" class="mb-5">
                    <span class="text-caption grey--text text--lighten-2 font-weight-bold d-block mb-2 text-uppercase letter-spacing-1">Option wählen</span>
                    <v-chip-group 
                        v-model="selectedVariation" 
                        class="premium-chip-group" 
                        mandatory
                    >
                        <v-chip 
                            v-for="(variation, index) of product.variations" 
                            :key="'chip' + index" 
                            :value="index"
                            class="theme--light"
                        >
                            <v-icon left size="10" :color="variation.color || '#00B074'">mdi-circle</v-icon>
                            {{ variation.title }}
                        </v-chip>
                    </v-chip-group>
                </div>

                <!-- Selected Option Description Panel -->
                <div class="mb-6 pa-4 rounded-xl" style="background: rgba(255,255,255,0.015); border: 1px solid rgba(255, 255, 255, 0.03);">
                    <h4 class="text-subtitle-2 font-weight-bold white--text mb-1">{{ activeVariation.title }}</h4>
                    <p class="grey--text text--lighten-1 mb-0 text-caption" style="line-height: 1.45;" v-html="activeVariation.description"></p>
                </div>

                <!-- Price Section -->
                <div class="mb-6">
                    <span class="text-caption grey--text text--lighten-2 d-block mb-2 text-uppercase letter-spacing-1">Wert</span>
                    
                    <!-- If variation allows custom amount -->
                    <div v-if="activeVariation.setOwnPrice">
                        <v-form v-model="formValid" class="mt-2">
                            <v-text-field 
                                label="Wunschbetrag eingeben" 
                                v-model="ownPrice" 
                                type="number" 
                                filled
                                rounded
                                dark
                                :rules="rules.minWorth" 
                                class="premium-input"
                                suffix="CHF"
                            ></v-text-field>
                        </v-form>
                    </div>
                    
                    <!-- Static price pill -->
                    <div v-else class="price-tag-pill d-inline-block">
                        {{ activeVariation.pricePrefix ? activeVariation.pricePrefix + ' ' : '' }}
                        CHF {{ activeVariation.price }}{{ activeVariation.priceSuffix ? '.-' + activeVariation.priceSuffix : '.-' }}
                    </div>
                </div>

                <!-- CTA Actions -->
                <div class="mt-8">
                    <v-btn 
                        @click="createNewProduct()" 
                        class="premium-btn py-7" 
                        block 
                        x-large
                    >
                        <v-icon left>mdi-cart-plus</v-icon>
                        {{ product.alternateToCartText ? product.alternateToCartText : 'In den Warenkorb' }}
                    </v-btn>
                    <p class="grey--text text--lighten-2 text-caption mb-0 mt-3 text-center" style="line-height: 1.4;">
                        {{ product.condition }}
                    </p>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import api from '../../api'
import { mapGetters, mapActions, mapState } from 'vuex'

export default {
    props: {
        product: { required: true, type: Object },
    },
    data() {
        return {
            rules: {
                minWorth: [(val) => val >= 5 || `Der Mindestwert ist 5 Franken.`],
            },
            shareButton: {
                color: 'white',
                icon: 'mdi-export-variant',
            },
            formValid: null,
            ownPrice: 0,
            selectedVariation: 0,
        }
    },
    methods: {
        ...mapActions('shopStore', ['addProductToCart']),
        createNewProduct() {
            if (this.product.shopExternLink) {
                if (confirm(this.product.externLinkPopupText)) {
                    this.$router.push({ path: this.product.shopExternLink, query: { previousRoute: '/shop' } })
                }
            } else {
                if (!this.activeVariation.setOwnPrice || (this.activeVariation.setOwnPrice && this.formValid)) {
                    let copyProduct = JSON.parse(JSON.stringify(this.product))

                    copyProduct.price = this.activeVariation.price || this.ownPrice
                    copyProduct.variation = this.product.activeVariations
                    this.addProductToCart(copyProduct)
                    this.$toast.success('Dein Produkt wurde in den Warenkorb gelegt.')
                    this.$emit('close')
                }
            }
        },
        shareProduct(product) {
            navigator.share({
                title: 'well come FIT ' + product.title,
                text: 'Hey, schau dir mal dieses Produkt an, das ich bei well come FIT gefunden habe! Ich dachte, es könnte dich auch interessieren.',
                url: 'https://app.wellcomefit.ch/shop/' + product._id,
            })
        },
    },
    computed: {
        dialogVisible: {
            get() {
                return !!this.product;
            },
            set(val) {
                if (!val) {
                    this.$emit('close');
                }
            }
        },
        activeVariation() {
            return this.product.variations[this.selectedVariation]
        },
    },
}
</script>

<style scoped>
/* High-End Dialog Window Override styling */
.glass-card-dialog {
    background: radial-gradient(circle at 100% 100%, #12251d 0%, #08090c 70%) !important;
    backdrop-filter: blur(24px) !important;
    -webkit-backdrop-filter: blur(24px) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 28px !important;
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.8) !important;
    position: relative;
}

/* Close Button styling inside card, floating on top of the image */
.close-btn {
    position: absolute;
    right: 16px;
    top: 16px;
    z-index: 100;
    background: rgba(8, 9, 12, 0.7) !important; /* Dark solid glass background to stand out on bright images */
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    backdrop-filter: blur(8px);
    border-radius: 50% !important;
    transition: all 0.3s ease;
}

.close-btn:hover {
    background: rgba(8, 9, 12, 0.9) !important;
    border-color: rgba(255, 255, 255, 0.3) !important;
    transform: scale(1.08);
}

.product-image-wrapper {
    width: 100%;
    position: relative;
    overflow: hidden;
}

.product-image {
    width: 100%;
    object-fit: cover;
}

/* Beautiful dark image transition gradient overlay */
.product-image-gradient-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(8, 9, 12, 0.95) 100%);
    z-index: 1;
}

.line-height-tight {
    line-height: 1.15;
}

.letter-spacing-1 {
    letter-spacing: 0.08em;
}

/* Ensure premium input layout styling */
::v-deep .premium-input .v-input__slot {
    background: rgba(255, 255, 255, 0.02) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Specific overrides for chips */
::v-deep .premium-chip-group .v-chip.v-chip.theme--light {
    background-color: rgba(255, 255, 255, 0.03) !important;
    color: #aaaaaa !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

::v-deep .premium-chip-group .v-chip.v-chip.theme--light.v-chip--active {
    background-color: rgba(0, 176, 116, 0.15) !important;
    border: 1px solid #00B074 !important;
    color: #00B074 !important;
}
</style>